import { apiRequest } from '@/api/http';
import { InboxPage, InboxReadResult } from '@/api/schemas/notifications';

/** The category every alert in `admin-alerts.subscriber.ts` is dispatched under. */
export const TEAM_ALERTS_CATEGORY = 'MODERATION';

/** The backend's page cap (`ListNotificationsQuerySchema`, `limit` max 50). */
export const TEAM_ALERTS_PAGE = 20;

/**
 * `GET /v1/notifications?category=MODERATION&limit=20` — the most recent
 * alerts the backend sent the signed-in admin, newest first.
 *
 * Self-service: the route is the user's own inbox behind the global JWT
 * guard, no admin role, no capability. What makes it the team's tray is who
 * the backend fans the alerts out to — every active ADMIN — not the route.
 * One page is the whole tray on purpose: the alerts are rare, bounded events
 * and the section lives on the home screen; a cursor would be a second page
 * nobody opens. `nextCursor` still travels, so the section can SAY there is
 * more rather than let twenty look like everything.
 */
export function listTeamAlerts(): Promise<InboxPage> {
  const p = new URLSearchParams({
    category: TEAM_ALERTS_CATEGORY,
    limit: String(TEAM_ALERTS_PAGE),
  });
  return apiRequest(`/notifications?${p.toString()}`, { schema: InboxPage });
}

/**
 * `POST /v1/notifications/:id/read` — stamp the row as read.
 *
 * Idempotent on the backend (a second read keeps the first `readAt`) and not
 * an admin write: no Idempotency-Key, no reason. It records that somebody on
 * the team saw the alert — it does NOT resolve what the alert is about, which
 * happens on the screen the row links to. Per row, never «read all»: the
 * backend's `read-all` sweeps the user's whole inbox, every category, which
 * is more than this section shows.
 */
export function markTeamAlertRead(id: string): Promise<InboxReadResult> {
  return apiRequest(`/notifications/${id}/read`, { method: 'POST', schema: InboxReadResult });
}
